<script setup>
import { computed, onMounted, ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import CompanyCustomAttributeItem from 'dashboard/components-next/Companies/CompanyDetail/CompanyCustomAttributeItem.vue';

const props = defineProps({
  company: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();

const searchQuery = ref('');
const loadError = ref('');
const loading = ref(false);
const companyAttributes = useMapGetter('attributes/getCompanyAttributes');

const customAttributes = computed(() => props.company?.customAttributes || {});
const hasCompanyAttributes = computed(
  () => companyAttributes.value?.length > 0
);

const processCompanyAttributes = (
  attributes,
  attributeValues,
  filterCondition
) => {
  if (!attributes.length) {
    return [];
  }

  return attributes.reduce((result, attribute) => {
    const { attributeKey } = attribute;

    if (filterCondition(attributeKey, attributeValues)) {
      result.push({
        ...attribute,
        value: attributeValues[attributeKey] ?? '',
      });
    }

    return result;
  }, []);
};

const usedAttributes = computed(() =>
  processCompanyAttributes(
    companyAttributes.value,
    customAttributes.value,
    (key, values) => key in values
  )
);

const unusedAttributes = computed(() =>
  processCompanyAttributes(
    companyAttributes.value,
    customAttributes.value,
    (key, values) => !(key in values)
  )
);

const filteredUnusedAttributes = computed(() =>
  unusedAttributes.value.filter(attribute =>
    attribute.attributeDisplayName
      .toLowerCase()
      .includes(searchQuery.value.toLowerCase())
  )
);

const unusedAttributesCount = computed(() => unusedAttributes.value.length);
const hasNoUnusedAttributes = computed(() => unusedAttributesCount.value === 0);
const hasNoUsedAttributes = computed(() => usedAttributes.value.length === 0);

const loadAttributes = async () => {
  if (loading.value) return;
  loading.value = true;
  loadError.value = '';
  try {
    await store.dispatch('attributes/get', { throwOnError: true });
  } catch {
    loadError.value = t('WORKSPACE_V2.COMPANY_DETAIL.LOAD_ERROR');
  } finally {
    loading.value = false;
  }
};
onMounted(loadAttributes);
</script>

<template>
  <p class="px-6 pt-4 text-xs text-n-slate-11">
    {{ t('WORKSPACE_V2.COMPANY_DETAIL.ATTRIBUTES_IMMEDIATE') }}
  </p>
  <div v-if="loadError" role="alert" class="px-6 text-sm text-n-ruby-11">
    <p>{{ loadError }}</p>
    <Button
      :label="t('WORKSPACE_V2.COMPANY_DETAIL.RETRY')"
      @click="loadAttributes"
    />
  </div>
  <p v-else-if="loading" role="status" class="px-6 text-sm text-n-slate-11">
    {{ t('COMPANIES.DETAIL.LOADING') }}
  </p>
  <div
    v-if="hasCompanyAttributes && !loading && !loadError"
    class="flex flex-col gap-6 px-6 py-6"
  >
    <div v-if="!hasNoUsedAttributes" class="flex flex-col gap-2">
      <CompanyCustomAttributeItem
        v-for="attribute in usedAttributes"
        :key="`${company.id}-${attribute.id}`"
        is-editing-view
        :company-id="company.id"
        :attribute="attribute"
      />
    </div>

    <div v-if="!hasNoUnusedAttributes" class="flex items-center gap-3">
      <div class="flex-1 h-px bg-n-slate-5" />
      <span class="text-sm font-medium text-n-slate-10">
        {{
          t('COMPANIES.DETAIL.ATTRIBUTES.UNUSED_ATTRIBUTES', {
            count: unusedAttributesCount,
          })
        }}
      </span>
      <div class="flex-1 h-px bg-n-slate-5" />
    </div>

    <div class="flex flex-col gap-3">
      <div v-if="!hasNoUnusedAttributes" class="relative">
        <span
          class="pointer-events-none absolute inset-y-0 flex items-center ltr:left-3 rtl:right-3"
        >
          <Icon
            icon="i-ph-magnifying-glass"
            aria-hidden="true"
            class="size-4 text-n-slate-11"
          />
        </span>
        <input
          v-model="searchQuery"
          type="search"
          class="reset-base !m-0 h-8 w-full rounded-xl bg-n-alpha-black2 px-2 py-2 text-sm text-n-slate-12 outline-none border-none dark:bg-n-solid-1 ltr:pl-10 rtl:pr-10"
          :aria-label="t('COMPANIES.DETAIL.ATTRIBUTES.SEARCH_PLACEHOLDER')"
          :placeholder="t('COMPANIES.DETAIL.ATTRIBUTES.SEARCH_PLACEHOLDER')"
        />
      </div>

      <div
        v-if="filteredUnusedAttributes.length === 0 && !hasNoUnusedAttributes"
        class="flex items-center justify-start h-11"
      >
        <p class="text-sm text-n-slate-11">
          {{ t('COMPANIES.DETAIL.ATTRIBUTES.NO_ATTRIBUTES') }}
        </p>
      </div>

      <div v-if="!hasNoUnusedAttributes" class="flex flex-col gap-2">
        <CompanyCustomAttributeItem
          v-for="attribute in filteredUnusedAttributes"
          :key="`${company.id}-${attribute.id}`"
          :company-id="company.id"
          :attribute="attribute"
        />
      </div>
    </div>
  </div>

  <p
    v-else-if="!loading && !loadError"
    class="px-6 py-10 text-sm leading-6 text-center text-n-slate-11"
  >
    {{ t('COMPANIES.DETAIL.ATTRIBUTES.EMPTY_STATE') }}
  </p>
</template>
